import { useState } from 'react';
import type { PaneData } from '../../shared/contracts';
import { ExternalLink, ChevronRight } from 'lucide-react';
import { PaneFrame } from './PaneFrame';
import { EmptyRow } from './States';

/**
 * "Anna Ansat" → "AA" — the fallback when a row photo is missing or fails to load.
 * A trailing collision suffix ("Anna Ansat (2)") is not part of the name.
 */
function initialsOf(name: string): string {
  const parts = name.replace(/\s*\(\d+\)$/, '').trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '')).toUpperCase() || '?';
}

/** Round row avatar: the proxied photo, or initials when it is missing/broken. */
function RowAvatar({ src, name }: { src: string; name: string }) {
  const [failed, setFailed] = useState(false);
  // Decorative: the row's name text is the accessible label, so the photo (and the
  // initials fallback) is hidden from assistive tech.
  if (failed) {
    return (
      <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-primary/10 text-xs font-semibold text-brand-primary">
        {initialsOf(name)}
      </span>
    );
  }
  return <img src={src} alt="" aria-hidden="true" onError={() => setFailed(true)} className="h-8 w-8 shrink-0 rounded-full object-cover" />;
}

/**
 * A list/ranking pane. Rows with an href link directly to their source record; rows
 * carrying a bucket (e.g. a ranked company) drill into their underlying records instead.
 */
export function ListCard({ pane, eyebrow, onDrill }: { pane: PaneData; eyebrow?: string; onDrill?: (bucket?: string) => void }) {
  const rows = pane.rows ?? [];
  const ranked = pane.kind === 'ranking';
  // A pane with a rich drill table opens it on card click (rows keep their own links).
  const onOpen = pane.table?.rows.length && onDrill ? () => onDrill() : undefined;
  return (
    <PaneFrame title={pane.title} source={pane.source} eyebrow={eyebrow} periodLabel={pane.periodLabel} onOpen={onOpen}>
      <ul className="divide-y divide-brand-primary/5">
        {rows.map((row, i) => {
          const content = (
            <>
              {ranked && <span className="w-5 font-heading text-sm italic text-brand-primary/40">{i + 1}</span>}
              {row.imageUrl && <RowAvatar src={row.imageUrl} name={row.title} />}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-brand-dark">{row.title}</div>
                {row.subtitle && <div className="truncate text-xs text-brand-dark/50">{row.subtitle}</div>}
              </div>
              <div className="text-right">
                {row.value && <div className="text-sm font-semibold text-brand-primary">{row.value}</div>}
                {row.meta && <div className="text-[11px] text-brand-dark/45">{row.meta}</div>}
              </div>
              {row.href ? (
                <ExternalLink size={13} className="ml-1 shrink-0 text-brand-accent opacity-0 transition-opacity group-hover/row:opacity-100" />
              ) : row.bucket && onDrill ? (
                <ChevronRight size={13} className="ml-1 shrink-0 text-brand-dark/30" />
              ) : null}
            </>
          );
          const cls = 'flex items-center gap-3 py-2.5';
          if (row.href) {
            return (
              <li key={row.id}>
                <a
                  href={row.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`group/row -mx-2 rounded-lg px-2 transition-colors hover:bg-brand-primary/5 ${cls}`}
                >
                  {content}
                </a>
              </li>
            );
          }
          if (row.bucket && onDrill) {
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDrill(row.bucket);
                  }}
                  className={`-mx-2 w-full rounded-lg px-2 text-left transition-colors hover:bg-brand-primary/5 ${cls}`}
                >
                  {content}
                </button>
              </li>
            );
          }
          return (
            <li key={row.id} className={cls}>
              {content}
            </li>
          );
        })}
        {rows.length === 0 && <EmptyRow label="Ingen data." />}
      </ul>
    </PaneFrame>
  );
}
